/**
 * Shows the app-wide RBAC notice (rbac/notices.ts): "your access changed",
 * "your session was ended", "you can't open this item". Rendered next to
 * <App/> (main.ts), so it works on every route — inside the dashboard
 * layout, on full-page routes (messages, TDrive, editors) and on the
 * sign-in page after a revoked session.
 */
import { defineComponent, h, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { ShieldExclamationIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import { dismissRbacNotice, NOTICE_AUTO_HIDE_MS, NOTICE_MESSAGE_KEYS, rbacNotice } from './notices';

export default defineComponent({
  name: 'RbacNoticeHost',
  setup() {
    const { t } = useI18n();
    let timer: ReturnType<typeof setTimeout> | null = null;

    function clearTimer() {
      if (timer) clearTimeout(timer);
      timer = null;
    }

    watch(
      rbacNotice,
      (notice) => {
        clearTimer();
        const delay = notice ? NOTICE_AUTO_HIDE_MS[notice.kind] : null;
        if (notice && delay) {
          timer = setTimeout(() => {
            if (rbacNotice.value?.id === notice.id) dismissRbacNotice();
          }, delay);
        }
      },
      { immediate: true }
    );

    onUnmounted(clearTimer);

    return () => {
      const notice = rbacNotice.value;
      if (!notice) return null;
      return h(
        'div',
        {
          class: 'fixed inset-x-0 z-[9998] flex justify-center px-4 pointer-events-none',
          style: { top: 'calc(env(safe-area-inset-top, 0px) + 4.25rem)' },
        },
        [
          h(
            'div',
            {
              'data-rbac-notice': notice.kind,
              role: 'status',
              'aria-live': 'polite',
              class:
                'pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-xl border border-amber-200 dark:border-amber-700 bg-white dark:bg-gray-800 px-4 py-3 shadow-sm',
            },
            [
              h(ShieldExclamationIcon, { class: 'mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500', 'aria-hidden': 'true' }),
              h('p', { class: 'flex-1 text-sm text-gray-700 dark:text-gray-200' }, t(NOTICE_MESSAGE_KEYS[notice.kind])),
              h(
                'button',
                {
                  type: 'button',
                  class:
                    'flex-shrink-0 rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                  'aria-label': t('rbac.dismiss'),
                  onClick: dismissRbacNotice,
                },
                [h(XMarkIcon, { class: 'h-4 w-4', 'aria-hidden': 'true' })]
              ),
            ]
          ),
        ]
      );
    };
  },
});
